// 引入 axios
import axios from "axios";
import store from "@/store";
import router from "@/router";
import { handleHttpAuthorizationError } from "@/Utils/httpAuthorization";

class Request {
  constructor(baseUrl = "") {
    this.baseUrl = baseUrl; // 基础 URL，用于拼接接口地址
    this.instance = axios.create({
      baseURL: this.baseUrl,
      timeout: 100000, // 请求超时时间
      headers: {
        "Content-Type": "application/json",
      },
    });

    // 添加请求拦截器
    this.instance.interceptors.request.use(
      (config) => {
        // 注入 Authorization header
        const token = store.state.token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // FormData 必须交给浏览器设置 multipart boundary，不能沿用 JSON 默认头。
        if (typeof FormData !== "undefined" && config.data instanceof FormData) {
          if (config.headers && typeof config.headers.delete === "function") {
            config.headers.delete("Content-Type");
          } else if (config.headers) {
            delete config.headers["Content-Type"];
            delete config.headers["content-type"];
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 添加响应拦截器
    this.instance.interceptors.response.use(
      (response) => {
        // 跳过 blob 类型的响应（文件下载）
        if (response.data instanceof Blob) {
          return Promise.resolve(response);
        }

        if (response.data.code !== 200) {
          const error = new Error(response.data.message || "请求失败");
          error.status = Number(response.data.code) || 0;
          return Promise.reject(error);
        }

        // 假设后端返回的都是 JSON 格式的字符串，这里统一处理
        if (typeof response.data.data === "string") {
          try {
            response.data.data = JSON.parse(response.data.data);
          } catch (error) {
            // 非 JSON 格式的字符串（如成功提示文字），保持原样
          }
        }
        return Promise.resolve(response);
      },
      (error) => {
        const status =
          (error.response && error.response.status) || error.status || 0;
        handleHttpAuthorizationError(status, store, router);
        return Promise.reject(error);
      }
    );
  }

  // 发起 GET 请求
  async get(endpoint, params = {}) {
    try {
      const response = await this.instance.get(endpoint, { params });
      return response.data; // 返回解析后的 JSON 数据
    } catch (error) {
      return this._handleError(error);
    }
  }

  // 发起 PUT 请求
  async put(endpoint, data = {}) {
    try {
      const response = await this.instance.put(endpoint, data);
      return response.data; // 返回解析后的 JSON 数据
    } catch (error) {
      return this._handleError(error);
    }
  }

  // 发起 POST 请求
  async post(endpoint, data = {}) {
    try {
      const response = await this.instance.post(endpoint, data);
      return response.data; // 返回解析后的 JSON 数据
    } catch (error) {
      return this._handleError(error);
    }
  }

  // 发起 PATCH 请求
  async patch(endpoint, data = {}) {
    try {
      const response = await this.instance.patch(endpoint, data);
      return response.data;
    } catch (error) {
      return this._handleError(error);
    }
  }

  // 发起 DELETE 请求
  async delete(endpoint) {
    try {
      const response = await this.instance.delete(endpoint);
      return response.data;
    } catch (error) {
      return this._handleError(error);
    }
  }

  // 处理错误
  _handleError(error) {
    const status =
      (error.response && error.response.status) || error.status || 0;
    if (error.response) {
      const responseData = error.response.data;
      const message =
        (responseData && responseData.message) || error.message || "请求失败";
      return { error: message, status };
    } else if (error.request) {
      return { error: "服务无响应，请检查网络连接", status };
    } else {
      return { error: error.message || "请求失败", status };
    }
  }
}

export default Request;

// // 使用示例
// const api = new Request("https://api.example.com");
//
// api
//   .get("/data", { id: 123 })
//   .then((data) => console.log("GET Response:", data))
//   .catch((error) => console.error("GET Error:", error));
//
// api
//   .post("/submit", { name: "John", age: 30 })
//   .then((data) => console.log("POST Response:", data))
//   .catch((error) => console.error("POST Error:", error));
